import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, GoogleAuthProvider, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, initializeFirestore, type Firestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

let authEmulatorConnected = false;
let firestoreEmulatorConnected = false;
let storageEmulatorConnected = false;
let firestoreClient: Firestore | null = null;

function shouldUseLocalEmulators() {
  return process.env.NEXT_PUBLIC_FIREBASE_USE_EMULATORS === 'true'
    && typeof window !== 'undefined'
    && ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

function emulatorPort(name: 'AUTH' | 'FIRESTORE' | 'STORAGE', fallback: number) {
  const configured = name === 'AUTH'
    ? process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_PORT
    : name === 'FIRESTORE'
      ? process.env.NEXT_PUBLIC_FIREBASE_FIRESTORE_EMULATOR_PORT
      : process.env.NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_PORT;
  const value = Number(configured);
  return Number.isInteger(value) && value > 0 && value <= 65535 ? value : fallback;
}

export function hasFirebaseClientEnv() {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.authDomain &&
      firebaseConfig.projectId &&
      firebaseConfig.storageBucket &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId,
  );
}

export function getFirebaseClientApp(): FirebaseApp | null {
  if (!hasFirebaseClientEnv()) return null;
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export function getFirebaseAuthClient(): Auth | null {
  const app = getFirebaseClientApp();
  if (!app) return null;
  const auth = getAuth(app);
  if (shouldUseLocalEmulators() && !authEmulatorConnected) {
    connectAuthEmulator(auth, `http://${window.location.hostname}:${emulatorPort('AUTH', 9099)}`, { disableWarnings: true });
    authEmulatorConnected = true;
  }
  return auth;
}

export function getFirestoreClient(): Firestore | null {
  const app = getFirebaseClientApp();
  if (!app) return null;
  if (!firestoreClient) {
    try {
      const localEmulator = shouldUseLocalEmulators();
      firestoreClient = initializeFirestore(app, localEmulator
        ? { experimentalForceLongPolling: true, experimentalLongPollingOptions: { timeoutSeconds: 30 } }
        : { experimentalAutoDetectLongPolling: true, experimentalLongPollingOptions: { timeoutSeconds: 30 } });
    } catch {
      // Hot reload or another Firebase module may have initialized the default instance already.
      firestoreClient = getFirestore(app);
    }
  }
  const firestore = firestoreClient;
  if (shouldUseLocalEmulators() && !firestoreEmulatorConnected) {
    connectFirestoreEmulator(firestore, window.location.hostname, emulatorPort('FIRESTORE', 8080));
    firestoreEmulatorConnected = true;
  }
  return firestore;
}


export function getStorageClient(): FirebaseStorage | null {
  const app = getFirebaseClientApp();
  if (!app) return null;
  const storage = getStorage(app);
  if (shouldUseLocalEmulators() && !storageEmulatorConnected) {
    connectStorageEmulator(storage, window.location.hostname, emulatorPort('STORAGE', 9199));
    storageEmulatorConnected = true;
  }
  return storage;
}

export function getGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}
